import { z } from 'zod';
import { FilterLeafSchema } from './views.js';
import { JiraPullModeSchema } from './jira.js';
/**
 * FR-12: автоматизации пространства.
 *
 * Правило (AutomationRule) — расписание (cron) + цепочка шагов (AutomationStep).
 * Шаги выполняются строго последовательно. По умолчанию ошибка шага
 * останавливает всю цепочку; шаг может включить `continueOnError`.
 *
 * Типы шагов:
 *   - 'items.update' — фильтр по типу записей + установка значений полей
 *   - 'jira.pull'    — авто-pull по JQL (как ручной bulk_jira_pull_jql)
 *   - 'jira.push'    — авто-push view'а в Jira (как ручной bulk_jira_push)
 *
 * Для jira-шагов `runAsUserId` обязателен — задаёт пользователя, по чьему
 * Jira-токену будут идти изменения. Пользователь должен быть участником
 * пространства и иметь сохранённый Jira-токен.
 */
// ─── Cron ────────────────────────────────────────────────────────────────
const cronField = z
    .string()
    .min(9, 'cron-выражение слишком короткое')
    .max(120, 'cron-выражение слишком длинное');
// ─── Action (для items.update) ──────────────────────────────────────────
export const AutomationActionSchema = z.object({
    fieldSlug: z.string().min(1).max(120),
    value: z.unknown().nullable(),
});
// ─── Step config'и ──────────────────────────────────────────────────────
export const ItemsUpdateStepConfigSchema = z.object({
    itemTypeId: z.string().uuid(),
    filter: z.array(FilterLeafSchema).default([]),
    actions: z.array(AutomationActionSchema).min(1, 'Нужно хотя бы одно действие'),
});
export const JiraPullStepConfigSchema = z.object({
    itemTypeId: z.string().uuid(),
    jql: z.string().min(1).max(8000),
    mode: JiraPullModeSchema.default('create_and_update'),
    runAsUserId: z.string().uuid(),
});
export const JiraPushStepConfigSchema = z.object({
    viewId: z.string().uuid(),
    changedSinceLastRun: z.boolean().default(true),
    runAsUserId: z.string().uuid(),
});
// ─── Step (входной) ─────────────────────────────────────────────────────
export const AutomationStepInputSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('items.update'),
        config: ItemsUpdateStepConfigSchema,
        continueOnError: z.boolean().optional().default(false),
    }),
    z.object({
        type: z.literal('jira.pull'),
        config: JiraPullStepConfigSchema,
        continueOnError: z.boolean().optional().default(false),
    }),
    z.object({
        type: z.literal('jira.push'),
        config: JiraPushStepConfigSchema,
        continueOnError: z.boolean().optional().default(false),
    }),
]);
export const AutomationStepTypeSchema = z.enum(['items.update', 'jira.pull', 'jira.push']);
// ─── Step DTO ───────────────────────────────────────────────────────────
export const AutomationStepSchema = z.object({
    id: z.string().uuid(),
    ruleId: z.string().uuid(),
    order: z.number().int().min(0),
    type: AutomationStepTypeSchema,
    config: z.record(z.unknown()),
    continueOnError: z.boolean(),
});
// ─── Rule DTO ───────────────────────────────────────────────────────────
export const AutomationRuleSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    name: z.string(),
    enabled: z.boolean(),
    cronExpression: z.string(),
    maxItemsPerRun: z.number().int().min(1).max(100000),
    lastRunAt: z.string().nullable(),
    nextRunAt: z.string().nullable(),
    lastJobId: z.string().uuid().nullable(),
    lastJobStatus: z.string().nullable(),
    lastError: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    steps: z.array(AutomationStepSchema),
});
// ─── Create / Update ────────────────────────────────────────────────────
export const CreateAutomationRuleRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    cronExpression: cronField,
    enabled: z.boolean().optional().default(true),
    maxItemsPerRun: z.number().int().min(1).max(100000).optional().default(1000),
    steps: z.array(AutomationStepInputSchema).min(1, 'Нужен хотя бы один шаг'),
});
export const UpdateAutomationRuleRequestSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    cronExpression: cronField.optional(),
    enabled: z.boolean().optional(),
    maxItemsPerRun: z.number().int().min(1).max(100000).optional(),
    steps: z.array(AutomationStepInputSchema).min(1).optional(),
});
// ─── Run / step run DTO ─────────────────────────────────────────────────
export const AutomationStepRunSchema = z.object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    stepId: z.string().uuid().nullable(),
    order: z.number().int(),
    type: AutomationStepTypeSchema,
    status: z.enum(['running', 'success', 'failed', 'skipped']),
    matchedCount: z.number().int(),
    updatedCount: z.number().int(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
    details: z.record(z.unknown()).nullable(),
});
export const AutomationRunSchema = z.object({
    id: z.string().uuid(),
    ruleId: z.string().uuid(),
    jobId: z.string().uuid().nullable(),
    status: z.enum(['running', 'success', 'failed', 'partial']),
    triggeredBy: z.enum(['schedule', 'manual']),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
    steps: z.array(AutomationStepRunSchema),
});
// ─── Job payload (kind='automation_run') ────────────────────────────────
export const AutomationRunJobPayloadSchema = z.object({
    ruleId: z.string().uuid(),
    triggeredBy: z.enum(['schedule', 'manual']),
    /**
     * Значение rule.lastRunAt ДО текущего триггера — т.е. время предыдущего
     * триггера правила. triggerRule сразу перезаписывает lastRunAt текущим
     * временем, поэтому watermark для changedSinceLastRun передаётся через
     * payload, а не читается из правила воркером.
     */
    prevRunAt: z.string().nullable(),
});
// ─── Run-now / preview-cron ─────────────────────────────────────────────
export const RunAutomationNowResponseSchema = z.object({
    jobId: z.string().uuid(),
    runId: z.string().uuid(),
    ruleId: z.string().uuid(),
});
